'use client';

import { c as _c } from "react/compiler-runtime";
import { Close as DialogClose, Content as DialogContent, Description as DialogDescription, Overlay as DialogOverlay, Portal as DialogPortal, Root as DialogRoot, Title as DialogTitle } from '@radix-ui/react-dialog';
import { ArrowRight, X } from 'lucide-react';
import { useState } from 'react';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { formatMoneyAmount } from '@/lib/money';
import { useBundle } from './bundle-context';
import { BundlePanel } from './bundle-panel';
interface BundleStickyBarProps {
  onCheckout: () => void;
  isSubmitting: boolean;
}
const MAX_SLOTS = 4;
export function BundleStickyBar(t0) {
  const $ = _c(60);
  const {
    onCheckout,
    isSubmitting
  } = t0;
  const {
    settings,
    preview,
    entries,
    moneyLocale
  } = useBundle();
  const [sheetOpen, setSheetOpen] = useState(false);
  if (!settings.stickyBar.enabled) {
    return null;
  }
  const isEmpty = preview.quantity === 0;
  const goal = preview.nextTier ? preview.nextTier.minQuantity : preview.quantity;
  let overflow;
  let placeholderCount;
  let statusLabel;
  let t1;
  let t2;
  let t3;
  if ($[0] !== entries || $[1] !== goal || $[2] !== preview.nextTier || $[3] !== preview.percentOff || $[4] !== preview.quantity || $[5] !== preview.remainingToNext || $[6] !== settings.stickyBar.emptyHint || $[7] !== settings.stickyBar.savingsTemplate) {
    const visibleEntries = entries.slice(0, MAX_SLOTS);
    overflow = entries.length - visibleEntries.length;
    placeholderCount = overflow > 0 ? 0 : Math.max(0, Math.min(MAX_SLOTS, Math.max(goal, preview.quantity, 1)) - visibleEntries.length);
    let t4;
    if ($[14] !== preview.nextTier || $[15] !== preview.percentOff || $[16] !== preview.remainingToNext || $[17] !== settings.stickyBar.emptyHint || $[18] !== settings.stickyBar.savingsTemplate) {
      t4 = preview.percentOff > 0 ? settings.stickyBar.savingsTemplate.replace("{percent}", String(preview.percentOff)) : preview.nextTier ? `Add ${preview.remainingToNext} more to save ${preview.nextTier.percentageDiscount}%` : settings.stickyBar.emptyHint;
      $[14] = preview.nextTier;
      $[15] = preview.percentOff;
      $[16] = preview.remainingToNext;
      $[17] = settings.stickyBar.emptyHint;
      $[18] = settings.stickyBar.savingsTemplate;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    statusLabel = t4;
    t3 = "fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+0.625rem)] pt-3 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] lg:hidden";
    t1 = "mb-2.5 flex items-center gap-1.5";
    t2 = visibleEntries.map(_temp);
    $[0] = entries;
    $[1] = goal;
    $[2] = preview.nextTier;
    $[3] = preview.percentOff;
    $[4] = preview.quantity;
    $[5] = preview.remainingToNext;
    $[6] = settings.stickyBar.emptyHint;
    $[7] = settings.stickyBar.savingsTemplate;
    $[8] = overflow;
    $[9] = placeholderCount;
    $[10] = statusLabel;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    overflow = $[8];
    placeholderCount = $[9];
    statusLabel = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
  }
  let t4;
  if ($[20] !== overflow || $[21] !== placeholderCount) {
    t4 = overflow > 0 ? <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-neutral-900 bg-neutral-900 text-xs font-bold text-white">+{overflow}</div> : Array.from({
      length: placeholderCount
    }).map(_temp2);
    $[20] = overflow;
    $[21] = placeholderCount;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== t1 || $[24] !== t2 || $[25] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== isEmpty || $[28] !== moneyLocale || $[29] !== preview.currencyCode || $[30] !== preview.percentOff || $[31] !== preview.subtotal || $[32] !== preview.total || $[33] !== statusLabel) {
    t6 = <div className="min-w-0">{isEmpty ? <p className="truncate text-xs font-bold uppercase tracking-wide text-neutral-700">{statusLabel}</p> : <><p className="flex items-baseline gap-1.5 text-sm font-bold text-neutral-900">{formatMoneyAmount(preview.total, preview.currencyCode, moneyLocale)}{preview.percentOff > 0 && <span className="text-xs font-medium text-neutral-400 line-through">{formatMoneyAmount(preview.subtotal, preview.currencyCode, moneyLocale)}</span>}</p><p className="truncate text-xs font-semibold text-brand-accent">{statusLabel}</p></>}</div>;
    $[27] = isEmpty;
    $[28] = moneyLocale;
    $[29] = preview.currencyCode;
    $[30] = preview.percentOff;
    $[31] = preview.subtotal;
    $[32] = preview.total;
    $[33] = statusLabel;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setSheetOpen(true);
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArrowRight className="h-4 w-4" />;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== isEmpty || $[38] !== settings.stickyBar.ctaLabel) {
    t9 = <button type="button" onClick={t7} disabled={isEmpty} className="flex shrink-0 items-center gap-2 rounded-md bg-brand-accent px-5 py-3 text-sm font-bold uppercase tracking-wider text-white transition-[filter] hover:brightness-110 disabled:opacity-50">{settings.stickyBar.ctaLabel}{t8}</button>;
    $[37] = isEmpty;
    $[38] = settings.stickyBar.ctaLabel;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== t6 || $[41] !== t9) {
    t10 = <div className="flex items-center justify-between gap-3">{t6}{t9}</div>;
    $[40] = t6;
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t3 || $[45] !== t5) {
    t11 = <div className={t3}>{t5}{t10}</div>;
    $[43] = t10;
    $[44] = t3;
    $[45] = t5;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DialogOverlay className="dialog-overlay fixed inset-0 z-50 bg-black/40 lg:hidden" />;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  let t14;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DialogTitle className="sr-only">Your bundle</DialogTitle>;
    t14 = <DialogDescription className="sr-only">Review the tees in your bundle and continue to checkout.</DialogDescription>;
    $[48] = t13;
    $[49] = t14;
  } else {
    t13 = $[48];
    t14 = $[49];
  }
  let t15;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DialogClose className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-neutral-600 shadow-sm transition-colors hover:bg-white hover:text-neutral-900"><X className="h-4 w-4" /><span className="sr-only">Close</span></DialogClose>;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== isSubmitting || $[52] !== onCheckout) {
    t16 = <DialogPortal>{t12}<DialogContent className="dialog-content fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-2xl bg-white shadow-2xl lg:hidden">{t13}{t14}{t15}<BundlePanel onAddToCart={onCheckout} isSubmitting={isSubmitting} className="rounded-none border-0 shadow-none" /></DialogContent></DialogPortal>;
    $[51] = isSubmitting;
    $[52] = onCheckout;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== sheetOpen || $[55] !== t16) {
    t17 = <DialogRoot open={sheetOpen} onOpenChange={setSheetOpen}>{t16}</DialogRoot>;
    $[54] = sheetOpen;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t11 || $[58] !== t17) {
    t18 = <>{t11}{t17}</>;
    $[57] = t11;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  return t18;
}
function _temp2(_, index) {
  return <div key={`bundle-slot-${index}`} className="h-10 w-10 shrink-0 rounded-md border border-dashed border-neutral-300 bg-neutral-50" />;
}
function _temp(entry) {
  return <div key={entry.variantId} className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md border border-neutral-200 bg-neutral-100">{entry.image?.url ? <ShopifyImage src={entry.image.url} alt={entry.image.altText || entry.title} fill={true} sizes="40px" className="object-cover" /> : null}</div>;
}